import React, { useState, useEffect } from 'react';
import { ChevronRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const WelcomePage = ({ onEnter }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  const handleEnter = () => {
    setFadeOut(true);
    setTimeout(() => {
      setIsVisible(false);
      onEnter();
    }, 1000);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`welcome-page fixed inset-0 z-[100] flex items-center justify-center overflow-hidden transition-opacity duration-1000 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Animated background */}
      <div className="absolute inset-0 bg-brand-brown">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-brown via-[#2a1519] to-brand-brown"></div>
        
        {/* Radial pulse effects synchronized with breathing */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute w-[200px] h-[200px] bg-brand-yellow/5 rounded-full blur-3xl animate-breath-pulse"></div>
          <div className="absolute w-[400px] h-[400px] bg-brand-olive/5 rounded-full blur-3xl animate-breath-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute w-[600px] h-[600px] bg-brand-yellow/5 rounded-full blur-3xl animate-breath-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute w-[800px] h-[800px] bg-brand-olive/5 rounded-full blur-3xl animate-breath-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>
        
        {/* Light rays emanating from center */}
        <div className="absolute inset-0 flex items-center justify-center opacity-20">
          <div className="ray ray-1"></div>
          <div className="ray ray-2"></div>
          <div className="ray ray-3"></div>
          <div className="ray ray-4"></div>
          <div className="ray ray-5"></div>
          <div className="ray ray-6"></div>
        </div>
        
        {/* Glow effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-olive/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-yellow/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-12 px-6">
        {/* Breathing emblem container */}
        <div className="emblem-container relative flex items-center justify-center">
          {/* Pulsing rings that expand outward */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[300px] h-[300px] border-2 border-brand-yellow/30 rounded-full animate-ring-expand"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[300px] h-[300px] border-2 border-brand-olive/20 rounded-full animate-ring-expand" style={{ animationDelay: '1s' }}></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[300px] h-[300px] border border-brand-yellow/20 rounded-full animate-ring-expand" style={{ animationDelay: '2s' }}></div>
          </div>
          
          {/* Intense glow effect behind logo - breathes with it */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-96 h-96 bg-gradient-radial from-brand-yellow/30 via-brand-olive/20 to-transparent rounded-full blur-3xl animate-emblem-breath"></div>
          </div>
          
          {/* Logo with dramatic breathing animation */}
          <div className="relative flex items-center justify-center">
            <div className="logo-wrapper relative animate-emblem-breath">
              {/* Outer glow ring */}
              <div className="absolute inset-0 flex items-center justify-center -z-10">
                <div className="w-[400px] h-[400px] bg-brand-yellow/10 rounded-full blur-2xl animate-emblem-breath"></div>
              </div>
              
              {/* Actual emblem logo */}
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_50a99d4b-16e5-4a25-a3c2-534efc6745c6/artifacts/9ur1riyu_d51c164a-21d7-4291-b45e-5c3d3e3a1260-1_all_5079.jpg" 
                  alt="JRT Chideme Media Emblem" 
                  className="relative w-80 h-80 object-cover rounded-full shadow-2xl border-4 border-brand-yellow/60"
                  style={{ 
                    filter: 'brightness(1.15) contrast(1.2) drop-shadow(0 0 40px rgba(236, 203, 51, 0.5))'
                  }}
                />
                
                {/* Animated corner sparkles */}
                <Sparkles className="absolute -top-4 -right-4 w-8 h-8 text-brand-yellow animate-twinkle" />
                <Sparkles className="absolute -bottom-4 -left-4 w-8 h-8 text-brand-yellow animate-twinkle" style={{ animationDelay: '0.5s' }} />
                <Sparkles className="absolute -top-4 -left-4 w-6 h-6 text-brand-olive animate-twinkle" style={{ animationDelay: '1s' }} />
                <Sparkles className="absolute -bottom-4 -right-4 w-6 h-6 text-brand-olive animate-twinkle" style={{ animationDelay: '1.5s' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Text content with fade-in animation */}
        <div className="text-center space-y-6 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight">
            <span className="text-brand-yellow">JRT</span> Chideme Media
          </h1>
          <p className="text-xl md:text-2xl text-brand-cream/80 max-w-2xl mx-auto">
            Empowering Digital Success Stories
          </p>
          
          {/* Animated divider */}
          <div className="flex items-center justify-center gap-2 py-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-brand-olive animate-expand"></div>
            <Sparkles className="w-5 h-5 text-brand-yellow animate-pulse" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-brand-olive animate-expand"></div>
          </div>
        </div>

        {/* Enter button with animation */}
        <div className="animate-fade-in-up" style={{ animationDelay: '1s' }}>
          <Button
            onClick={handleEnter}
            size="lg"
            className="group bg-brand-yellow hover:bg-brand-yellow/90 text-brand-brown font-bold px-12 py-6 text-xl rounded-full transition-all duration-500 hover:scale-110 hover:shadow-2xl shadow-brand-yellow/50 animate-bounce-subtle"
          >
            Enter Experience
            <ChevronRight className="ml-2 w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
          </Button>
          
          <p className="text-center text-brand-cream/50 text-sm mt-4 animate-pulse">
            Click to continue
          </p>
        </div>
      </div>

      {/* Cinematic vignette effect */}
      <div className="absolute inset-0 pointer-events-none bg-radial-gradient"></div>
    </div>
  );
};

export default WelcomePage;
